import { useQuestionAnalytics } from "../../../hooks/useQuestionAnalytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers, Question } from "../../../types/onboarding";
import { GenderDropdown } from "../GenderDropdown";
import { PlanningDropdown } from "../PlanningDropdown";
import styles from "./GenderRelationshipQuestion.module.css";

interface GenderRelationshipQuestionProps {
	question: Question;
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | string[]) => void;
}

export function GenderRelationshipQuestion({
	question,
	answers,
	onInputChange,
}: GenderRelationshipQuestionProps) {
	const { t } = useClientTranslations("onboarding");
	const { trackSelection } = useQuestionAnalytics(question);

	// Map gender display values to backend values
	const mapGender = (displayValue: string): string => {
		const mapping: Record<string, string> = {
			[t("questions.genderRelationship.genderOptions.male")]: "Male",
			[t("questions.genderRelationship.genderOptions.female")]: "Female",
			[t("questions.genderRelationship.genderOptions.preferNotToSay")]:
				"Not specified",
			[t("questions.genderRelationship.genderOptions.nonBinary")]: "Non-binary",
		};
		return mapping[displayValue] || displayValue;
	};

	// Reverse mapping for gender display
	const reverseMapGender = (backendValue: string): string => {
		const reverseMapping: Record<string, string> = {
			Male: t("questions.genderRelationship.genderOptions.male"),
			Female: t("questions.genderRelationship.genderOptions.female"),
			"Not specified": t(
				"questions.genderRelationship.genderOptions.preferNotToSay",
			),
			"Non-binary": t("questions.genderRelationship.genderOptions.nonBinary"),
		};
		return reverseMapping[backendValue] || backendValue;
	};

	// Map display values to backend values for relationship status
	const mapRelationshipStatus = (displayValue: string): string => {
		const mapping: Record<string, string> = {
			[t("questions.genderRelationship.relationshipOptions.singleParent")]:
				"Single parent",
			[t(
				"questions.genderRelationship.relationshipOptions.heterosexualCouple",
			)]: "Heterosexual couple",
			[t(
				"questions.genderRelationship.relationshipOptions.sameSexFemaleCouple",
			)]: "Same sex female",
			[t("questions.genderRelationship.relationshipOptions.sameSexMaleCouple")]:
				"Same sex male",
		};
		return mapping[displayValue] || displayValue;
	};

	// Reverse mapping for display
	const reverseMapRelationshipStatus = (backendValue: string): string => {
		const reverseMapping: Record<string, string> = {
			"Single parent": t(
				"questions.genderRelationship.relationshipOptions.singleParent",
			),
			"Heterosexual couple": t(
				"questions.genderRelationship.relationshipOptions.heterosexualCouple",
			),
			"Same sex female": t(
				"questions.genderRelationship.relationshipOptions.sameSexFemaleCouple",
			),
			"Same sex male": t(
				"questions.genderRelationship.relationshipOptions.sameSexMaleCouple",
			),
			"Same sex couple": t(
				"questions.genderRelationship.relationshipOptions.sameSexFemaleCouple",
			), // Legacy support
		};
		return reverseMapping[backendValue] || backendValue;
	};

	return (
		<div className={styles.container}>
			<div className={styles.genderSection}>
				<GenderDropdown
					value={reverseMapGender(answers.gender || "")}
					onChange={(value) => {
						const backendValue = mapGender(value);
						onInputChange("gender", backendValue);
						trackSelection(backendValue, undefined, "gender");
					}}
				/>
			</div>

			<div className={styles.relationshipSection}>
				<label className={styles.label} htmlFor="relationshipStatus">
					{t("questions.genderRelationship.parentingPath")}
				</label>
				<PlanningDropdown
					value={reverseMapRelationshipStatus(answers.relationshipStatus || "")}
					onChange={(value) => {
						const backendValue = mapRelationshipStatus(value);
						onInputChange("relationshipStatus", backendValue);
						trackSelection(backendValue, undefined, "relationship");
					}}
				/>
			</div>
		</div>
	);
}
